'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import JourneyNav from '@/components/JourneyNav';
import NextBridgeCTA from '@/components/NextBridgeCTA';
import LensHero from './components/LensHero';
import NamesSearchOverlay from '@/components/NamesSearchOverlay';
import IRLWalkthroughSection from './components/IRLWalkthroughSection';
import IRLUnifiedScreen from './components/IRLUnifiedScreen';
import BridgeRGTransition from './components/BridgeRGTransition';
import { CollaborativeObservationMode, BatinDepthUnlock } from './components/CollaborativeObservation';
import HaalSnapshot from './components/HaalSnapshot';
import Link from 'next/link';
import { Verse2164Mapping, ForestWatershedDescent, HikmahStationEmergence } from './components/Plot3IRLEnhancements';
import { IRLLensSelfReferential } from '../open-letter/components/Plot5Enhancements';
import BiomeRingDeepDive from './components/BiomeRingDeepDive';

// ── Four-Beat IRL Quick Mode ──────────────────────────────────────────────────

function FourBeatQuickMode() {
  const [beats, setBeats] = React.useState({ looking: '', seeing: '', recalling: '', recognizing: '' });
  const [activeBeat, setActiveBeat] = React.useState(0);
  const [saved, setSaved] = React.useState(false);

  const BEAT_DATA = [
    { id: 'looking', arabic: 'لَمْحَة', label: 'Looking', sublabel: 'Hawas activation', desc: 'Raw sensory contact. What do you perceive right now?', color: '#2d7a47', yaqeen: 'Ilm' },
    { id: 'seeing', arabic: 'رُؤْيَة', label: 'Seeing', sublabel: 'Zahir registration', desc: 'The thing is perceived as a thing with form, location, state.', color: '#c9a84c', yaqeen: 'Ayn' },
    { id: 'recalling', arabic: 'تَذَكُّر', label: 'Recalling', sublabel: 'Names surface', desc: 'The vocabulary of recognition activates. What Names come to mind?', color: '#8a8ab8', yaqeen: 'Ayn → Haqq' },
    { id: 'recognizing', arabic: 'مَعْرِفَة', label: 'Recognizing', sublabel: 'Haqq arrives', desc: 'The thing is known not as itself but as an Ayah — a sign pointing back to its Source.', color: '#2d8a87', yaqeen: 'Haqq' },
  ] as const;

  const handleSave = () => {
    if (typeof window !== 'undefined') {
      const existing = JSON.parse(localStorage.getItem('haal_four_beat_entries') || '[]');
      existing.push({ ...beats, timestamp: new Date().toISOString() });
      localStorage.setItem('haal_four_beat_entries', JSON.stringify(existing.slice(-20)));
    }
    setSaved(true);
    setTimeout(() => { setSaved(false); setBeats({ looking: '', seeing: '', recalling: '', recognizing: '' }); setActiveBeat(0); }, 2000);
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(201,168,76,0.2)', background: 'rgba(6,13,18,0.8)' }}>
      {/* Header */}
      <div className="p-5" style={{ background: 'rgba(201,168,76,0.06)', borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>IRL Lens · Field Quick-Mode</p>
            <h3 className="font-mono text-base font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Four-Beat Observation Protocol</h3>
          </div>
          <span className="font-mono text-[9px] px-2 py-1 rounded" style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.2)' }}>
            Daily Ward Walk
          </span>
        </div>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.6 }}>
          "After some time, you're handed a lens, and we go around again, <em>looking — seeing, recalling — recognizing.</em>" — The Open Letter. The rapid observation protocol for daily ward walks. Four breaths per encounter.
        </p>
      </div>

      {/* Beat selector */}
      <div className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {BEAT_DATA.map((beat, idx) => (
          <button
            key={beat.id}
            onClick={() => setActiveBeat(idx)}
            className="flex-1 py-3 transition-all"
            style={{
              background: activeBeat === idx ? `${beat.color}10` : 'transparent',
              borderBottom: activeBeat === idx ? `2px solid ${beat.color}` : '2px solid transparent',
            }}
          >
            <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: activeBeat === idx ? beat.color : 'rgba(212,197,169,0.3)' }}>{beat.label}</p>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: activeBeat === idx ? `${beat.color}80` : 'rgba(212,197,169,0.2)', direction: 'rtl' }}>{beat.arabic}</p>
          </button>
        ))}
      </div>

      {/* Active beat */}
      <div className="p-5">
        {BEAT_DATA.map((beat, idx) => idx === activeBeat && (
          <div key={beat.id}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs font-black" style={{ background: `${beat.color}20`, color: beat.color, border: `1px solid ${beat.color}40` }}>
                {idx + 1}
              </div>
              <div>
                <p className="font-mono text-sm font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>{beat.label}</p>
                <p className="font-mono text-[9px]" style={{ color: `${beat.color}70` }}>{beat.sublabel} · Yaqeen: {beat.yaqeen}</p>
              </div>
            </div>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}>{beat.desc}</p>
            <textarea
              value={beats[beat.id as keyof typeof beats]}
              onChange={e => setBeats(prev => ({ ...prev, [beat.id]: e.target.value }))}
              rows={3}
              placeholder={`${beat.label}...`}
              className="w-full rounded-lg px-3 py-2 font-mono text-xs resize-none"
              style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${beat.color}25`, color: 'rgba(212,197,169,0.7)', outline: 'none' }}
            />
            <div className="flex justify-between mt-3">
              <button
                onClick={() => setActiveBeat(Math.max(0, idx - 1))}
                disabled={idx === 0}
                className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 transition-all"
                style={{ color: idx === 0 ? 'rgba(212,197,169,0.2)' : 'rgba(212,197,169,0.5)', border: `1px solid ${idx === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.1)'}` }}
              >
                ← Back
              </button>
              {idx < BEAT_DATA.length - 1 ? (
                <button
                  onClick={() => setActiveBeat(idx + 1)}
                  className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 transition-all"
                  style={{ background: `${beat.color}15`, color: beat.color, border: `1px solid ${beat.color}30` }}
                >
                  Next Beat →
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  className="font-mono text-[9px] uppercase tracking-widest px-4 py-1.5 transition-all"
                  style={{ background: saved ? 'rgba(45,122,71,0.2)' : 'rgba(201,168,76,0.15)', color: saved ? '#2d7a47' : '#c9a84c', border: `1px solid ${saved ? 'rgba(45,122,71,0.4)' : 'rgba(201,168,76,0.3)'}` }}
                >
                  {saved ? '✓ Saved to Ilm Dock' : 'Save Observation →'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer link to full 8-layer */}
      <div className="px-5 pb-4">
        <p className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.3)' }}>
          This is the field quick-form. For deep study, use the{' '}
          <a href="#lens-tool" style={{ color: '#c9a84c', textDecoration: 'none' }}>full 8-layer IRL pathway →</a>
        </p>
      </div>
    </div>
  );
}

// ── Open Letter CTA for IRL page ──
function IRLOpenLetterCTA() {
  return (
    <section
      style={{
        background: 'linear-gradient(180deg, #0d0d1a 0%, #060d12 100%)',
        padding: '64px 24px',
        borderTop: '1px solid rgba(138,138,184,0.1)',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <p
          style={{
            fontFamily: "'Amiri', serif",
            fontSize: 28,
            color: 'rgba(138,138,184,0.45)',
            direction: 'rtl',
            marginBottom: 8,
          }}
        >
          وَفِي أَنفُسِكُمْ ۚ أَفَلَا تُبْصِرُونَ
        </p>
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: 9,
            letterSpacing: '.15em',
            color: 'rgba(138,138,184,0.3)',
            marginBottom: 28,
          }}
        >
          "And in yourselves — do you not see?" — Quran 51:21
        </p>
        <h3
          style={{
            fontFamily: "'Georgia', serif",
            fontSize: 'clamp(1.1rem, 3vw, 1.5rem)',
            color: '#F5F0E8',
            fontWeight: 400,
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          The lens you have just used has a root.
        </h3>
        <p
          style={{
            color: 'rgba(212,197,169,0.45)',
            fontSize: 13,
            lineHeight: 1.9,
            maxWidth: 500,
            margin: '0 auto 32px',
          }}
        >
          The IRL Lens, the Recognition Arc, the Names — all of it flows from a foundational letter written to imprisoned caretakers. The crossing begins there.
        </p>
        <Link
          href="/open-letter"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '16px 36px',
            background: 'rgba(138,138,184,0.08)',
            border: '1px solid rgba(138,138,184,0.25)',
            borderRadius: 4,
            color: '#8a8ab8',
            fontFamily: 'monospace',
            fontSize: 10,
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            textDecoration: 'none',
            transition: 'background 0.2s, border-color 0.2s',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.background = 'rgba(138,138,184,0.14)';
            (e.currentTarget as HTMLElement).style.borderColor = 'rgba(138,138,184,0.45)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.background = 'rgba(138,138,184,0.08)';
            (e.currentTarget as HTMLElement).style.borderColor = 'rgba(138,138,184,0.25)';
          }}
        >
          <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: 'rgba(138,138,184,0.6)' }}>عُبُور</span>
          <span>Read the Foundational Open Letter</span>
          <span style={{ opacity: 0.5 }}>→</span>
        </Link>
      </div>
    </section>
  );
}

export default function InnerRealityLensClient() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [lensVisible, setLensVisible] = useState(false);
  const [unifiedOpen, setUnifiedOpen] = useState(false);
  const [plot3Tab, setPlot3Tab] = useState<'watershed' | '2164' | 'hikmah'>('watershed');
  const lensRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleNameSelect = useCallback(() => {}, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLensVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    if (lensRef?.current) observer?.observe(lensRef?.current);
    return () => observer?.disconnect();
  }, []);

  // Scroll into panel when it opens
  useEffect(() => {
    if (unifiedOpen && panelRef.current) {
      setTimeout(() => {
        panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }, [unifiedOpen]);

  return (
    <div className="min-h-screen deep-green-bg">
      <Header onSearchOpen={() => setSearchOpen(true)} />
      <JourneyNav currentPage="inner-reality-lens" />
      <main>
        <LensHero onUseLens={() => {
          setUnifiedOpen(true);
          // also scroll to lens-tool section
          setTimeout(() => {
            document.getElementById('lens-tool')?.scrollIntoView({ behavior: 'smooth' });
          }, 50);
        }} />

        {/* ── Visual Separator ── */}
        <div style={{
          position: 'relative',
          height: 80,
          background: 'linear-gradient(to bottom, #1C2B25, #0d1a14)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            height: 1,
            background: 'linear-gradient(to right, transparent, rgba(179,138,42,0.4) 20%, rgba(179,138,42,0.6) 50%, rgba(179,138,42,0.4) 80%, transparent)',
          }} />
          <div style={{
            position: 'relative',
            zIndex: 1,
            background: '#0d1a14',
            padding: '0 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.2em', color: 'rgba(179,138,42,0.5)', textTransform: 'uppercase' }}>
              ✦ LENS INTERFACE ✦
            </span>
          </div>
        </div>

        {/* IRL LENS INTERFACE — fade-in on scroll reveal */}
        <div
          id="lens-tool"
          ref={lensRef}
          style={{
            opacity: lensVisible ? 1 : 0,
            transform: lensVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          {/* IRLUnifiedScreen unfolding panel */}
          <div
            ref={panelRef}
            style={{
              maxHeight: unifiedOpen ? '9999px' : 0,
              overflow: 'hidden',
              transition: 'max-height 0.7s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            {unifiedOpen && (
              <div style={{ position: 'relative' }}>
                {/* Close / collapse bar */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 24px',
                  background: 'rgba(6,13,18,0.95)',
                  borderBottom: '1px solid rgba(179,138,42,0.2)',
                }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em', color: 'rgba(179,138,42,0.6)', textTransform: 'uppercase' }}>
                    ✦ IRL Unified Screen — Active
                  </span>
                  <button
                    onClick={() => setUnifiedOpen(false)}
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 9,
                      letterSpacing: '.12em',
                      color: 'rgba(245,240,232,0.35)',
                      background: 'none',
                      border: '1px solid rgba(255,255,255,0.1)',
                      padding: '4px 12px',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.7)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.35)'; }}
                  >
                    ✕ Collapse
                  </button>
                </div>
                <IRLUnifiedScreen />
              </div>
            )}
          </div>

          {/* Toggle button when panel is closed */}
          {!unifiedOpen && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '32px 24px',
              background: 'rgba(6,13,18,0.6)',
              borderBottom: '1px solid rgba(179,138,42,0.1)',
            }}>
              <button
                onClick={() => setUnifiedOpen(true)}
                style={{
                  fontFamily: 'monospace',
                  fontSize: 10,
                  letterSpacing: '.2em',
                  color: '#F5F0E8',
                  background: 'rgba(139,105,20,0.85)',
                  border: '1px solid rgba(179,138,42,0.4)',
                  padding: '12px 32px',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.85)'; }}
              >
                ✦ Open IRL Unified Screen
              </button>
            </div>
          )}

          <IRLWalkthroughSection />
        </div>

        {/* ── Collaborative Observation Mode + Batin Depth Unlock ── */}
        <section
          style={{
            background: 'linear-gradient(160deg, #060d12 0%, #0a1018 100%)',
            padding: 'clamp(48px, 6vw, 80px) clamp(16px, 4vw, 32px)',
            borderTop: '1px solid rgba(45,138,135,0.12)',
          }}
        >
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            {/* Section header */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', textTransform: 'uppercase', padding: '4px 12px', background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.3)', color: '#2d8a87', borderRadius: 4 }}>
                  Community Lens Features
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', textTransform: 'uppercase', padding: '4px 12px', background: 'rgba(90,90,138,0.08)', border: '1px solid rgba(90,90,138,0.25)', color: '#8a8ab8', borderRadius: 4 }}>
                  Batin Depth System
                </span>
              </div>
              <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)', color: '#F5F0E8', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: 8 }}>
                Collaborative Observation & Depth Unlock
              </h2>
              <p style={{ fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)', color: 'rgba(245,240,232,0.5)', lineHeight: 1.8, maxWidth: 640 }}>
                Multiple caretakers annotate the same Ayat from different vantage points. Hidden layers of the lens are progressively revealed as you complete more observations — from the visible surface (Zahir) to the hidden reality (Batin).
              </p>
            </div>

            {/* Three-column layout: Haal Snapshot + Collaborative + Batin */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 20, marginBottom: 20 }}>
              {/* Haal Snapshot */}
              <div style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 10, padding: 20 }}>
                <HaalSnapshot />
              </div>
              <CollaborativeObservationMode />
              <BatinDepthUnlock />
            </div>
          </div>
        </section>

        {/* ── Plot 3: Mural Watershed + 2:164 + Hikmah Stations ── */}
        <section
          style={{
            background: 'linear-gradient(160deg, #060d12 0%, #080f14 100%)',
            padding: 'clamp(48px, 6vw, 80px) clamp(16px, 4vw, 32px)',
            borderTop: '1px solid rgba(201,168,76,0.1)',
          }}
        >
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            {/* Four-Beat Quick Mode — field protocol */}
            <div style={{ marginBottom: 32 }}>
              <FourBeatQuickMode />
            </div>

            {/* ── BIOME RING DEEP DIVE ── */}
            <div style={{ marginBottom: 40, background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 12, padding: '24px' }}>
              <BiomeRingDeepDive />
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', textTransform: 'uppercase', padding: '4px 12px', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c', borderRadius: 4 }}>
                  Mural Architecture
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', textTransform: 'uppercase', padding: '4px 12px', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', color: '#2d7a47', borderRadius: 4 }}>
                  Plot 3 Enhancements
                </span>
              </div>
              <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)', color: '#F5F0E8', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: 8 }}>
                Watershed Descent · 2:164 Mapping · Hikmah Stations
              </h2>
              <p style={{ fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)', color: 'rgba(245,240,232,0.5)', lineHeight: 1.8, maxWidth: 640 }}>
                The Forest IS the IRL Lens expressed as geography. Each layer of the watershed corresponds to a layer of the recognition pathway. 2:164 is the spiral's source code — every domain of the Mural contained in one verse.
              </p>
            </div>

            {/* Tab selector */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
              {[
                { id: 'watershed', label: '⬡ Forest Watershed', color: '#2d7a47' },
                { id: '2164', label: '✦ 2:164 Mapping', color: '#c9a84c' },
                { id: 'hikmah', label: '◎ Hikmah Stations', color: '#D4A82A' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setPlot3Tab(tab.id as any)}
                  style={{
                    background: plot3Tab === tab.id ? `${tab.color}15` : 'rgba(6,13,18,0.5)',
                    border: `1px solid ${plot3Tab === tab.id ? `${tab.color}40` : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: 4,
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: 9,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: plot3Tab === tab.id ? tab.color : 'rgba(212,197,169,0.45)',
                    transition: 'all 0.15s',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {plot3Tab === 'watershed' && <ForestWatershedDescent />}
            {plot3Tab === '2164' && <Verse2164Mapping />}
            {plot3Tab === 'hikmah' && <HikmahStationEmergence />}
          </div>
        </section>

        {/* IRL Lens Self-Referential Meta-Recognition */}
        <section style={{ maxWidth: 900, margin: '0 auto', padding: '0 clamp(12px, 4vw, 24px) 40px' }}>
          <IRLLensSelfReferential />
        </section>

        {/* Bridge 03 transitional element — From Self to Community */}
        <BridgeRGTransition />
        {/* Open Letter CTA — foundational document */}
        <IRLOpenLetterCTA />
        <NextBridgeCTA currentPage="inner-reality-lens" />
      </main>
      <Footer />
      <NamesSearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onNameSelect={handleNameSelect}
      />
    </div>
  );
}
